import {
  FIX_FINDING_ID_PARAM,
  TRIAGE_FINDING_ID_PARAM,
} from './components/drawers/use-navigate-scan-finding-drawers.ts'
import type { RepositoryDetailsPageTab } from './routes/repository-details-page/repository-details-page.tsx'
import { buildPath } from './utils/build-path'
export type { RepositoryDetailsPageTab }

/** The detection sources the Detections tab can open on, and the query param that carries one. */
export const DETECTION_SOURCES = ['generated-rules', 'ai-detection'] as const
export type DetectionSource = (typeof DETECTION_SOURCES)[number]
export const DETECTION_SOURCE_PARAM = 'detectionSource'

const REPOSITORY_DETAILS_PATH = '/repositories/:repositoryId' as const
const ANALYSIS_DETAILS_DEPRECATED_PATH = '/analysis/:analysisId/:scanId' as const
const ANALYSIS_DETAILS_PATH = '/analysis/:analysisId' as const
const ANALYSIS_FIX_DRAWER_DEPRECATED_PATH = '/analysis/:analysisId/fix/:findingId' as const
const ANALYSIS_FIX_DRAWER_PATH = '/analysis/:analysisId/finding/:findingId/fixes' as const
const ANALYSIS_TRIAGE_DRAWER_PATH = '/analysis/:analysisId/finding/:findingId/triage' as const
const SCAN_DETAILS_PATH = '/scans/:scanId' as const

export const Routes = {
  LoginPage: { path: '/login', createPath: () => '/login', isRoute: (path: string) => /^\/login$/.test(path) },
  IntroPage: { path: '/intro', createPath: () => '/intro', isRoute: (path: string) => /^\/intro$/.test(path) },
  WelcomePage: { path: '/welcome', createPath: () => '/welcome', isRoute: (path: string) => /^\/welcome$/.test(path) },
  RepositoriesPage: {
    path: '/repositories' as const,
    createPath: () => '/repositories' as const,
    isRoute: (path: string) => /^\/repositories$/.test(path),
    navSection: 'repositories' as const,
  },
  HomePage: {
    path: '/',
    createPath: () => '/',
    isRoute: (path: string) => /^\/$/.test(path),
    navSection: 'repositories' as const,
  },
  RepositoryDetailsPage: {
    path: REPOSITORY_DETAILS_PATH,
    createPath: ({
      repositoryId,
      tab,
      detectionSource,
    }: {
      repositoryId: string
      tab?: RepositoryDetailsPageTab
      /** Which source the Detections tab opens on; ignored unless `tab` is `detections`. */
      detectionSource?: DetectionSource
    }) =>
      `${buildPath(REPOSITORY_DETAILS_PATH, { repositoryId })}${tab ? `?tab=${tab}` : ''}${
        tab === 'detections' && detectionSource ? `&${DETECTION_SOURCE_PARAM}=${detectionSource}` : ''
      }`,
    isRoute: (path: string) => /^\/repositories\/[^/]+$/.test(path),
    navSection: 'repositories' as const,
  },
  AnalysisDetailsPageDeprecated: {
    path: ANALYSIS_DETAILS_DEPRECATED_PATH,
    createPath: ({ analysisId, scanId }: { analysisId: string; scanId: string }) =>
      buildPath(ANALYSIS_DETAILS_DEPRECATED_PATH, { analysisId, scanId }),
    isRoute: (path: string) => /^\/analysis\/[^/]+\/[^/]+$/.test(path),
  },
  AnalysisDetailsPage: {
    path: ANALYSIS_DETAILS_PATH,
    createPath: ({ analysisId, queryString }: { analysisId: string; queryString?: string }) =>
      `${buildPath(ANALYSIS_DETAILS_PATH, { analysisId })}${queryString ? `?${queryString}` : ''}`,
    isRoute: (path: string) => /^\/analysis\/[^/]+$/.test(path),
  },
  AnalysisDetailsPageFindingFixDrawerDeprecated: {
    path: ANALYSIS_FIX_DRAWER_DEPRECATED_PATH,
    createPath: ({
      analysisId,
      findingId,
      queryString,
    }: {
      analysisId: string
      findingId: string
      queryString?: string
    }) =>
      `${buildPath(ANALYSIS_FIX_DRAWER_DEPRECATED_PATH, { analysisId, findingId })}${queryString ? `?${queryString}` : ''}`,
    isRoute: (path: string) => /^\/analysis\/[^/]+\/fix\/[^/]+$/.test(path),
  },
  AnalysisDetailsPageFindingFixDrawer: {
    path: ANALYSIS_FIX_DRAWER_PATH,
    createPath: ({
      analysisId,
      findingId,
      queryString,
    }: {
      analysisId: string
      findingId: string
      queryString?: string
    }) => `${buildPath(ANALYSIS_FIX_DRAWER_PATH, { analysisId, findingId })}${queryString ? `?${queryString}` : ''}`,
    isRoute: (path: string) => /^\/analysis\/[^/]+\/finding\/[^/]+\/fixes$/.test(path),
  },
  AnalysisDetailsPageFindingTriageDrawer: {
    path: ANALYSIS_TRIAGE_DRAWER_PATH,
    createPath: ({
      analysisId,
      findingId,
      queryString,
    }: {
      analysisId: string
      findingId: string
      queryString?: string
    }) => `${buildPath(ANALYSIS_TRIAGE_DRAWER_PATH, { analysisId, findingId })}${queryString ? `?${queryString}` : ''}`,
    isRoute: (path: string) => /^\/analysis\/[^/]+\/finding\/[^/]+\/triage$/.test(path),
  },
  ScansOverviewPage: {
    path: '/scans/' as const,
    createPath: () => '/scans/' as const,
    isRoute: (path: string) => /^\/scans\/$/.test(path),
    navSection: 'scans' as const,
  },
  ScanDetailsPage: {
    path: SCAN_DETAILS_PATH,
    /**
     * Optionally deep-links straight into a finding's drawer. The drawers are
     * query-param driven (see `useNavigateScanFindingDrawers`), so a finding is
     * addressed by the `(scanId, findingId)` pair rather than by a path of its
     * own. Pass `fixFindingId` to land on the fix, `triageFindingId` to land on
     * the analysis.
     */
    createPath: ({
      scanId,
      triageFindingId,
      fixFindingId,
    }: {
      scanId: string
      triageFindingId?: string
      fixFindingId?: string
    }) => {
      const path = buildPath(SCAN_DETAILS_PATH, { scanId })
      const params = new URLSearchParams([
        ...(triageFindingId ? [[TRIAGE_FINDING_ID_PARAM, triageFindingId]] : []),
        ...(fixFindingId ? [[FIX_FINDING_ID_PARAM, fixFindingId]] : []),
      ])
      return params.size > 0 ? `${path}?${params}` : path
    },
    isRoute: (path: string) => /^\/scans\/[^/]+$/.test(path),
    navSection: 'repositories' as const,
  },
  SecurityPosturePage: {
    path: '/security-posture' as const,
    createPath: () => '/security-posture' as const,
    isRoute: (path: string) => /^\/security-posture$/.test(path),
    navSection: 'security-posture' as const,
    isLabs: true as const,
  },
  OrganizationPage: {
    path: '/organization' as const,
    createPath: () => '/organization' as const,
    isRoute: (path: string) => /^\/organization$/.test(path),
    navSection: 'organization' as const,
  },
  OrganizationPageDeprecated: {
    path: '/preferences' as const,
    createPath: () => '/preferences' as const,
    isRoute: (path: string) => /^\/preferences$/.test(path),
  },
}

export type RouteKey = keyof typeof Routes

export type NavSection = 'repositories' | 'scans' | 'organization' | 'security-posture'

export function getNavSection(pathname: string): NavSection | undefined {
  return Object.values(Routes)
    .filter((route): route is typeof route & { navSection: NavSection } => 'navSection' in route)
    .find(route => route.isRoute(pathname))?.navSection
}
